import React, { useState, useEffect } from 'react';
import './App.css';
import logoImg from './assets/logo.jpeg';

import Gallery from './components/Gallery';
import EBrochure from './components/Ebrochure';
import Home from './components/Home';
import AboutPage from './components/AboutPage';
import ProductsPage from './components/ProductsPage';
import ContactPage from './components/ContactPage';
import ProductDetailsPage from './components/ProductDetailsPage';
import AdminPanel from './components/Admin';
import ProgressiveBackToTop from './components/ProgressiveBackToTop';
import LanguageSelector from './components/LanguageSelector';
import SitemapPage from './components/SitemapPage';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';

import Footer from './components/Footer';
import AutoTranslator, { changeWebsiteLanguage, retriggerTranslation } from './components/AutoTranslator';
import { api } from './api';
import Loader from './components/Loader';

function App() {
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isImagesLoaded, setIsImagesLoaded] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showNavbar, setShowNavbar] = useState(true);

  // Sync language with the googtrans cookie
  const [language, setLanguage] = useState(() => {
    const match = document.cookie.match(/googtrans=\/en\/([^;]+)/);
    return match ? match[1] : 'en';
  });

  // ✅ INITIAL PAGE DETECTION
  const [currentPage, setCurrentPage] = useState(() => {
    const path = window.location.pathname.toLowerCase().replace('/', '');
    const validPages = ['about', 'products', 'contact', 'gallery', 'admin', 'ebrochure', 'sitemap'];
    return validPages.includes(path) ? path : 'home';
  });

  const [currentCategory, setCurrentCategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // ✅ LOAD LIVE DATA ON STARTUP WITH STATIC FALLBACK
  useEffect(() => {
    // If admin page, we don't need to block loading of public data
    if (currentPage === 'admin') {
      setIsDataLoaded(true);
      return;
    }

    const loadBackendData = async () => {
      try {
        const [products, categories] = await Promise.all([
          api.getProducts(),
          api.getCategories()
        ]);
        if (products && products.length > 0) {
          localStorage.setItem('ndtools_products', JSON.stringify(products));
        }
        if (categories && categories.length > 0) {
          localStorage.setItem('ndtools_categories', JSON.stringify(categories));
        }
      } catch (err) {
        console.warn("Backend offline or failed to fetch, using cached/static data:", err);
      } finally {
        setIsDataLoaded(true);
      }
    };
    loadBackendData();
  }, [currentPage]);

  // ✅ MONITOR ALL IMAGE LOADING (PRODUCTION-GRADE FAIL-SAFE PRELOADER)
  useEffect(() => {
    if (!isDataLoaded) return;

    let activeCleanups = [];
    let timer = null;

    timer = setTimeout(() => {
      const imgElements = Array.from(document.querySelectorAll('img'));
      if (imgElements.length === 0) {
        setIsImagesLoaded(true);
        return;
      }

      let loadedCount = 0;
      const totalImages = imgElements.length;

      const checkImageFinished = () => {
        loadedCount++;
        if (loadedCount >= totalImages) {
          setIsImagesLoaded(true);
          // Run cleanups to free memory
          activeCleanups.forEach(cleanup => cleanup());
          activeCleanups = [];
        }
      };

      imgElements.forEach((img) => {
        // Bulletproof check if image is already loaded and parsed successfully from cache
        if (img.complete && img.naturalWidth !== 0) {
          checkImageFinished();
        } else {
          const handleFinished = () => {
            img.removeEventListener('load', handleFinished);
            img.removeEventListener('error', handleFinished);
            checkImageFinished();
          };
          img.addEventListener('load', handleFinished);
          img.addEventListener('error', handleFinished);

          // Add to registry for cleanup on unmount
          activeCleanups.push(() => {
            img.removeEventListener('load', handleFinished);
            img.removeEventListener('error', handleFinished);
          });
        }
      });
    }, 150);

    return () => {
      if (timer) clearTimeout(timer);
      activeCleanups.forEach(cleanup => cleanup());
    };
  }, [isDataLoaded]);

  // ✅ SCROLL HANDLER
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setShowNavbar(false);
      } else {
        setShowNavbar(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // ✅ URL SYNC
  useEffect(() => {
    const targetPath = currentPage === 'home' ? '' : currentPage;
    window.history.pushState(null, '', `/${targetPath}`);
  }, [currentPage]);

  // ✅ BACK/FORWARD SUPPORT
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname.toLowerCase().replace('/', '');
      const validPages = ['about', 'products', 'contact', 'gallery', 'ebrochure', 'admin', 'sitemap'];
      setCurrentPage(validPages.includes(path) ? path : 'home');
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // ✅ NAVIGATION FUNCTION
  const navigateTo = (pageName, data = null) => {
    setCurrentCategory(null);
    if (pageName === 'productDetails') {
      setSelectedProduct(data);
    }
    setCurrentPage(pageName);
    window.scrollTo({ top: 0, behavior: 'instant' });
    // Re-trigger Google Translate on the new page content
    retriggerTranslation();
  };

  // ✅ NAVBAR CLICK HANDLER
  const scrollToSection = (id) => {
    setIsMobileMenuOpen(false);
    if (id === 'home') { navigateTo('home'); return; }
    if (id === 'about') { navigateTo('about'); return; }
    if (id === 'products') { navigateTo('products'); return; }
    if (id === 'contact') { navigateTo('contact'); return; }
    if (id === 'gallery') { navigateTo('gallery'); return; }
    if (id === 'ebrochure') { navigateTo('ebrochure'); return; }
  };

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    changeWebsiteLanguage(langCode);
  };

  // ── ADMIN PAGE — standalone layout ──
  if (currentPage === 'admin') {
    return <AdminPanel navigateTo={navigateTo} />;
  }

  // ── LOADING SCREEN ──
  if (!isDataLoaded) {
    return <Loader text="Initializing live catalog..." />;
  }

  return (
    <div className="app-container">
      {/* Preloader Overlay for Images */}
      {!isImagesLoaded && <Loader text="Initializing live catalog..." />}

      {/* Auto Translation Engine */}
      <AutoTranslator />

      {/* NAVBAR */}
      <nav className={`navbar ${showNavbar ? 'show' : 'hide'}`}>
        <div
          className="nav-brand"
          onClick={() => scrollToSection('home')}
          style={{ cursor: 'pointer' }}
        >
          <img src={logoImg} alt="N.D.Tools Logo" className="nav-logo" />
          {/* <div className="nav-brand-text-container">
            <span className="nav-brand-main">N.D.</span>
            <span className="nav-brand-sub">TOOLS</span>
          </div> */}
        </div>

        <div
          className="mobile-menu-btn"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
        </div>

        <div className={`nav-links ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
          <span onClick={() => scrollToSection('home')} className={`nav-link ${currentPage === 'home' ? 'active' : ''}`}>Home</span>
          <span onClick={() => scrollToSection('about')} className={`nav-link ${currentPage === 'about' ? 'active' : ''}`}>About</span>
          <span onClick={() => scrollToSection('products')} className={`nav-link ${currentPage === 'products' || currentPage === 'productDetails' ? 'active' : ''}`}>Products</span>
          <span onClick={() => scrollToSection('gallery')} className={`nav-link ${currentPage === 'gallery' ? 'active' : ''}`}>Gallery</span>
          <span onClick={() => scrollToSection('ebrochure')} className={`nav-link ${currentPage === 'ebrochure' ? 'active' : ''}`}>E-Brochure</span>
          <span onClick={() => scrollToSection('contact')} className={`nav-link ${currentPage === 'contact' ? 'active' : ''}`}>Contact</span>

          <LanguageSelector
            currentLanguage={language}
            onLanguageChange={handleLanguageChange}
          />
        </div>
      </nav>

      <div className="navbar-spacer"></div>

      {/* Floating Circular Stamp Badge */}
      <div className="rotating-stamp-badge">
        <div className="stamp-text-wrapper">
          <svg viewBox="0 0 100 100" width="130" height="130">
            <defs>
              <path
                id="circlePath"
                d="M 50, 50 m -37, 0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0"
              />
            </defs>
            <circle cx="50" cy="50" r="37" fill="rgba(255, 255, 255, 0.05)" stroke="var(--accent-color)" strokeWidth="1.5" />
            <text fontSize="7.5" fontWeight="bold" fill="var(--accent-color)" letterSpacing="1.5">
              <textPath href="#circlePath">
                N.D. TOOLS • PREMIUM QUALITY • EST 2005 •
              </textPath>
            </text>
          </svg>
        </div>
        <div className="stamp-logo-wrapper">
          <img src={logoImg} alt="N.D. Tools Logo" />
        </div>
      </div>

      {/* ROUTING */}
      {currentPage === 'home' && <Home navigateTo={navigateTo} />}
      {currentPage === 'about' && <AboutPage />}
      {currentPage === 'products' && (
        <ProductsPage
          currentCategory={currentCategory}
          navigateTo={navigateTo}
        />
      )}
      {currentPage === 'contact' && <ContactPage navigateTo={navigateTo} />}
      {currentPage === 'productDetails' && (
        <ProductDetailsPage
          product={selectedProduct}
          navigateTo={navigateTo}
        />
      )}
      {currentPage === 'gallery' && <Gallery navigateTo={navigateTo} />}
      {currentPage === 'ebrochure' && <EBrochure navigateTo={navigateTo} />}
      {currentPage === 'sitemap' && <SitemapPage navigateTo={navigateTo} />}

      {/* FOOTER */}
      <Footer navigateTo={navigateTo} />

      {/* FLOATING BUTTONS */}
      <div className="floating-actions">
        <ProgressiveBackToTop />
        <a
          href="https://wa.me/919723962464"
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-btn"
        >
          <span className="whatsapp-btn-text">Chat with us</span>
          <FontAwesomeIcon
            icon={faWhatsapp}
            style={{ fontSize: '28px', color: '#25D366' }}
          />
        </a>
      </div>
    </div>
  );
}

export default App;